/** OAuth2 provider setup metadata for the Connections dashboard. */

export const OAUTH_REDIRECT_PATH = '/connections/oauth/callback';

export const OAUTH_PENDING_STORAGE_KEY = 'snagcart_oauth_pending';

export const OAUTH_APP_STORAGE_PREFIX = 'snagcart_oauth_app_';

export const OAUTH_PROVIDERS_META = {
  google: {
    label: 'Google',
    developerPortalUrl: 'https://console.cloud.google.com/apis/credentials',
    docsUrl: 'https://developers.google.com/identity/protocols/oauth2/web-server',
    setupSteps: [
      'Create an OAuth 2.0 Client ID (Web application) in Google Cloud Console.',
      'Add the redirect URI shown below under Authorized redirect URIs.',
      'Enable the APIs you need (Gmail, Calendar, etc.) for your project.',
    ],
    defaultScopes: ['openid', 'email', 'profile', 'https://www.googleapis.com/auth/calendar.readonly'],
    scopePlaceholder: 'openid email profile https://www.googleapis.com/auth/calendar.readonly',
  },
  hubspot: {
    label: 'HubSpot',
    developerPortalUrl: 'https://developers.hubspot.com/',
    docsUrl: 'https://developers.hubspot.com/docs/api/oauth-quickstart-guide',
    setupSteps: [
      'Create a HubSpot developer app and note the Client ID and Client Secret.',
      'Add the redirect URI shown below to your app\'s Redirect URLs.',
      'Request the CRM scopes your automations need.',
    ],
    defaultScopes: ['crm.objects.contacts.read', 'crm.objects.contacts.write', 'crm.objects.deals.read'],
    scopePlaceholder: 'crm.objects.contacts.read crm.objects.contacts.write',
  },
  gmail: {
    label: 'Gmail',
    developerPortalUrl: 'https://console.cloud.google.com/apis/credentials',
    docsUrl: 'https://developers.google.com/gmail/api/guides',
    setupSteps: [
      'Enable Gmail API in Google Cloud Console.',
      'Create OAuth 2.0 Client ID (Web) and add the redirect URI below.',
      'Connect and sign in with the Google account to use.',
    ],
    defaultScopes: ['https://www.googleapis.com/auth/gmail.readonly', 'openid', 'email', 'profile'],
    scopePlaceholder: 'openid email profile https://www.googleapis.com/auth/gmail.readonly',
  },
  outlook: {
    label: 'Outlook',
    developerPortalUrl: 'https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade',
    docsUrl: 'https://learn.microsoft.com/en-us/graph/auth-v2-user',
    setupSteps: [
      'Register an app in Azure AD → App registrations.',
      'Add redirect URI and Microsoft Graph permissions (Mail.Read, User.Read).',
      'Create client secret and connect.',
    ],
    defaultScopes: ['openid', 'email', 'profile', 'offline_access', 'Mail.Read', 'User.Read'],
    scopePlaceholder: 'openid email profile offline_access Mail.Read User.Read',
  },
  'slack-oauth': {
    label: 'Slack',
    developerPortalUrl: 'https://api.slack.com/apps',
    docsUrl: 'https://api.slack.com/authentication/oauth-v2',
    setupSteps: [
      'Create a Slack app and add OAuth redirect URL below.',
      'Add bot scopes and install app to workspace.',
      'Connect Slack and approve permissions.',
    ],
    defaultScopes: ['chat:write', 'channels:read'],
    scopePlaceholder: 'chat:write channels:read',
  },
  salesforce: {
    label: 'Salesforce',
    developerPortalUrl: 'https://developer.salesforce.com/',
    docsUrl: 'https://help.salesforce.com/s/articleView?id=sf.connected_app_create.htm',
    setupSteps: [
      'Create a Connected App in Salesforce Setup.',
      'Enable OAuth and set callback URL to the redirect URI below.',
      'Use Consumer Key and Secret or platform Connect.',
    ],
    defaultScopes: ['api', 'refresh_token', 'offline_access'],
    scopePlaceholder: 'api refresh_token offline_access',
  },
  github: {
    label: 'GitHub',
    developerPortalUrl: 'https://github.com/settings/developers',
    docsUrl: 'https://docs.github.com/en/apps/oauth-apps/building-oauth-apps',
    setupSteps: [
      'Register a new OAuth App under GitHub Developer Settings.',
      'Set the Authorization callback URL to the redirect URI shown below.',
      'Generate a client secret and paste both values here.',
    ],
    defaultScopes: ['repo', 'read:user', 'user:email'],
    scopePlaceholder: 'repo read:user user:email',
  },
};

export function getOAuthProviderMeta(providerId) {
  return OAUTH_PROVIDERS_META[providerId] || {
    label: providerId,
    developerPortalUrl: '',
    docsUrl: '',
    setupSteps: ['Register an OAuth app with the provider and add the redirect URI below.'],
    defaultScopes: [],
    scopePlaceholder: '',
  };
}

export function getOAuthRedirectUri() {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}${OAUTH_REDIRECT_PATH}`;
}

export function parseScopesInput(raw) {
  return (raw || '')
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function formatScopes(scopes) {
  if (Array.isArray(scopes)) return scopes.join(' ');
  return String(scopes || '');
}
